import { useEffect, useState } from "react";
import styles from "../../../styles/tables.module.scss";

import { EnvironmentsAPI } from "@providers/EnvironmentsAPI";
import { Button } from "../../components/Button";
import { Drawer } from "../../components/Drawer";
import { Empty } from "../../components/Empty";
import { Input } from "../../components/Input";
import { Label } from "../../components/Label";
import { Toggle } from "../../components/Toggle";
import { If } from "../../components/control-statements/If";
import { Icon } from "../../components/core/Icon";
import { EnvironmentStorage } from "../../tools/EnvironmentStorage";
import { QRCode } from "../../tools/QRCode";

const TABLE_STEPS = {
  CREATE: 1,
  UPDATE: 2,
};

export default function NetLogicTables({
  tables,
  loadTables,
  showCreateTable,
  onCloseCreateTable,
}) {
  const [state, setState] = useState({
    step: TABLE_STEPS.CREATE,
    loading: false,
    qrcodeImage: "",
    showEditTable: false,
    selectedTable: null,
  });

  const environment = EnvironmentStorage.getEnvironment();

  useEffect(() => {
    if (loadTables) {
      loadTables();
    }
  }, []);

  useEffect(() => {
    if (showCreateTable) {
      setState((prevState) => ({ ...prevState, step: TABLE_STEPS.CREATE }));
    }
  }, [showCreateTable]);

  const generateQrCode = async (table) => {
    const qrCodeData = await QRCode.getDataUrl(table.qrcode);
    setState((prevState) => ({ ...prevState, qrcodeImage: qrCodeData }));
  };

  const downloadQrCode = (table) => {
    QRCode.downloadQRCodeImage(`qrcode-${table.code}.png`, table.qrcode);
  };

  const editTable = async () => {
    setState((prevState) => ({ ...prevState, loading: true }));

    const { id, name, code, active } = state?.selectedTable;
    const value = { name, code, active };
    const response = await EnvironmentsAPI.putTablesEnvironment(
      environment.id,
      value,
      id
    );
    const enabledCode = response.getCode();
    if (enabledCode === 200 && loadTables) {
      await loadTables();
      setState((prevState) => ({
        ...prevState,
        showEditTable: false,
        selectedTable: null,
      }));
    }
    setState((prevState) => ({ ...prevState, loading: false }));
  };

  const createTable = async () => {
    setState((prevState) => ({ ...prevState, loading: true }));

    const { name, external_id } = state?.selectedTable;
    const value = { name, external_id };
    const response = await EnvironmentsAPI.createTablesEnvironment(
      environment.id,
      value
    );
    const enabledCode = response.getCode();
    if (enabledCode === 200 && loadTables) {
      await loadTables();
      if (onCloseCreateTable) {
        onCloseCreateTable();
      }
    }
    setState((prevState) => ({ ...prevState, loading: false }));
  };

  const openDrawer = (table) => {
    generateQrCode(table);

    setState((prevState) => ({
      ...prevState,
      step: TABLE_STEPS.UPDATE,
      showEditTable: true,
      selectedTable: table,
    }));
  };

  const validTable = (table) => {
    return table?.name && table?.external_id;
  };

  const { loading, step, showEditTable, qrcodeImage, selectedTable } = state;

  return (
    <div className={styles.schedulingContainer}>
      <>
        {tables?.length === 0 ? (
          <Empty>
            <Icon name="table-cells" size="4x" />
            <p>Nenhuma mesa cadastrada</p>
            <span>
              Atualmente, o sistema não possui nenhuma mesa registrada.
            </span>
          </Empty>
        ) : (
          <div className={styles.groupContainer}>
            {tables?.map((table) => (
              <div className={styles.group} key={table.id}>
                <div className={styles.header}>
                  <div className="d-flex align-center">
                    <div className={styles.icon}>
                      <Icon name="table-cells" size="large" />
                    </div>
                    <hr className={styles.verticalDivider} />
                    <p className={styles.title}>
                      {table.name || "Nome não cadastrado"}
                    </p>
                    <div
                      className={styles.edit}
                      onClick={() => openDrawer(table)}
                    >
                      <Icon name="pen" size="large" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </>
      <Drawer
        visible={showCreateTable || showEditTable}
        closeInFade={false}
        title={step === TABLE_STEPS.CREATE ? "Criar Mesa" : "Editar Mesa"}
        onClose={() => {
          if (step === TABLE_STEPS.UPDATE) {
            setState((prevState) => ({
              ...prevState,
              showEditTable: !prevState.showEditTable,
            }));
          }
          if (step === TABLE_STEPS.CREATE && onCloseCreateTable) {
            onCloseCreateTable();
          }
          setTimeout(() => {
            setState((prevState) => ({
              ...prevState,
              selectedTable: null,
            }));
          }, 500);
        }}
        footer={
          <Button
            disabled={loading || !validTable(selectedTable)}
            loading={loading}
            design="primary"
            className="w-100"
            onClick={(e) => {
              if (step === TABLE_STEPS.CREATE) {
                createTable(e);
              }
              if (step === TABLE_STEPS.UPDATE) {
                editTable(e);
              }
            }}
          >
            Salvar{step === TABLE_STEPS.UPDATE ? " Alteração" : ""}
          </Button>
        }
      >
        <Input
          floatLabel="Nome da mesa"
          design="two"
          value={selectedTable?.name}
          onChange={(e) =>
            setState((prevState) => ({
              ...prevState,
              selectedTable: {
                ...prevState.selectedTable,
                name: e.target.value,
              },
            }))
          }
          className="mt-15"
        />
        <Input
          floatLabel="Código da mesa"
          design="two"
          value={selectedTable?.external_id}
          onChange={(e) =>
            setState((prevState) => ({
              ...prevState,
              selectedTable: {
                ...prevState.selectedTable,
                external_id: e.target.value,
              },
            }))
          }
          className="mt-15"
        />
        <If cond={step === TABLE_STEPS.UPDATE}>
          <div className="mt-15 d-flex flex-column align-start">
            <Label>Status da mesa</Label>
            <Toggle
              active={Boolean(selectedTable?.active)}
              onChange={(value) =>
                setState((prevState) => ({
                  ...prevState,
                  selectedTable: {
                    ...prevState.selectedTable,
                    active: value ? 1 : 0,
                  },
                }))
              }
            />
          </div>
        </If>
        <If cond={step === TABLE_STEPS.UPDATE}>
          <div className={styles.qrcode}>
            <Label>QrCode</Label>
            <div className={styles.qrcodeContainer}>
              <img
                src={qrcodeImage}
                className={`${styles["img-fluid"]} ${styles.mobile}`}
                alt="Integrações"
                id="qrcode-image"
              />
              <Button
                design="primary"
                onClick={() => downloadQrCode(selectedTable)}
                className={"w-100"}
              >
                Salvar QR Code
              </Button>
            </div>
          </div>
        </If>
      </Drawer>
    </div>
  );
}
